/**
 * ExclusiveAccordion Custom Element
 * Exclusive accordion: only one item open at a time, all closed by default.
 * Uses buttons for triggers. CSS handles all animation via interpolate-size.
 */
if (!customElements.get("exclusive-accordion")) {
  class ExclusiveAccordion extends HTMLElement {
    /** @type {AbortController|null} */
    #abortController = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.#setupEventListeners();
    }

    disconnectedCallback() {
      if (this.#abortController) {
        this.#abortController.abort();
        this.#abortController = null;
      }
    }

    /**
     * Attaches click listeners to all toggle buttons.
     * @private
     */
    #setupEventListeners() {
      const { signal } = this.#abortController;

      this.querySelectorAll('[data-component="accordion-trigger"]').forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();
          const item = trigger.closest('[data-component="accordion-item"]');
          if (item) this.#toggle(item);
        }, { signal });
      });
    }

    /**
     * Toggles an accordion item open/closed.
     * If opening, closes all other items first.
     * @param {HTMLElement} targetItem
     * @private
     */
    #toggle(targetItem) {
      const isOpen = targetItem.hasAttribute("data-open");

      // Close all other items
      this.querySelectorAll('[data-component="accordion-item"][data-open]').forEach((item) => {
        if (item !== targetItem) {
          this.#close(item);
        }
      });

      // Toggle the target
      if (isOpen) {
        this.#close(targetItem);
      } else {
        this.#open(targetItem);
      }
    }

    /**
     * Opens an accordion item.
     * @param {HTMLElement} item
     * @private
     */
    #open(item) {
      const trigger = item.querySelector('[data-component="accordion-trigger"]');
      item.setAttribute("data-open", "");
      if (trigger) trigger.setAttribute("aria-expanded", "true");
    }

    /**
     * Closes an accordion item.
     * @param {HTMLElement} item
     * @private
     */
    #close(item) {
      const trigger = item.querySelector('[data-component="accordion-trigger"]');
      item.removeAttribute("data-open");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
    }
  }

  customElements.define("exclusive-accordion", ExclusiveAccordion);
}
